<script setup>
import { addThousandsSeparator, getPriceChangeClass } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import KaspaLogo from '~/components/svg/KaspaLogo.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    price: { type: Number, default: 0 },
    hashrateChange7d: { type: Number, default: 0 },
    hashrateChange30d: { type: Number, default: 0 },
    hashrateChange90d: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 cards-gap">
        <!-- Current Price Card -->
        <Card class="flex items-center justify-between">
            <div class="flex items-center space-x-5">
                <div class="p-3 bg-[#70C7BA]/10 dark:bg-[#70C7BA]/5 rounded-xl">
                    <KaspaLogo class="w-12 h-12" />
                </div>
                <div class="flex flex-col">
                    <div class="flex items-center mb-1.5">
                        <CardLabel text="Current Price" />
                        <InfoTooltip text="The current price of KAS in USD" />
                    </div>
                    <div>
                        <LoadingSkeleton v-if="props.loading" height="36px" width="140px" />
                        <h2 v-else class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#70C7BA] to-[#4A8D83] slide-in-up">
                            ${{ addThousandsSeparator(props.price, 4) }}
                        </h2>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Card -->
        <Card>
            <div class="flex items-center">
                <div class="flex items-center space-x-5 w-full">
                    <div class="p-3 bg-yellow-400/10 dark:bg-yellow-500/5 rounded-xl">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-12 h-12 text-yellow-400 dark:text-yellow-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <div class="flex flex-col flex-1">
                        <div class="flex items-center mb-3">
                            <CardLabel text="Hashrate Changes" />
                            <InfoTooltip text="Network hashrate changes over different time periods" />
                        </div>
                        <div class="grid grid-cols-3 gap-4 w-full">
                            <div class="flex flex-col items-center space-y-1">
                                <span class="text-xs text-gray-500 dark:text-gray-400">7d</span>
                                <LoadingSkeleton v-if="props.loading" height="28px" width="60px" />
                                <div v-else class="slide-in-up">
                                    <span class="text-lg font-bold" :class="getPriceChangeClass(props.hashrateChange7d)">
                                        {{ addThousandsSeparator(props.hashrateChange7d, 2) }}%
                                    </span>
                                </div>
                            </div>

                            <div class="flex flex-col items-center space-y-1">
                                <span class="text-xs text-gray-500 dark:text-gray-400">30d</span>
                                <LoadingSkeleton v-if="props.loading" height="28px" width="60px" />
                                <div v-else class="slide-in-up">
                                    <span class="text-lg font-bold" :class="getPriceChangeClass(props.hashrateChange30d)">
                                        {{ addThousandsSeparator(props.hashrateChange30d, 2) }}%
                                    </span>
                                </div>
                            </div>

                            <div class="flex flex-col items-center space-y-1">
                                <span class="text-xs text-gray-500 dark:text-gray-400">90d</span>
                                <LoadingSkeleton v-if="props.loading" height="28px" width="60px" />
                                <div v-else class="slide-in-up">
                                    <span class="text-lg font-bold" :class="getPriceChangeClass(props.hashrateChange90d)">
                                        {{ addThousandsSeparator(props.hashrateChange90d, 2) }}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>